import React from 'react'
import Rx from 'rx'
import styled from '@emotion/styled'
import { hexColors } from '@mobi/settings/hexColors'
import { Accordion } from '@mobi/component-library/Base/Accordion'
import type { AllUpSelection } from '@mobi/betslip/types'
import { observeImmutable } from '@core/Components/HOCs'
import { state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { state$ as formulaState$, ToggleFormula, FormulaState } from './driver'

type Props = {
  betPlaced: boolean
  formulas: AllUpSelection['formulas']
}

const state$ = Rx.Observable.combineLatest(
  quickbetState$,
  formulaState$,
  (quickbetStateRecord, formulaStateRecord): Props => {
    const { betPlaced } = quickbetStateRecord
    const { formulas } = formulaStateRecord.toJS() as FormulaState
    return { betPlaced, formulas }
  }
)

export const QuickbetFormula: React.ComponentClass = observeImmutable(state$, ({ record }) => {
  const { betPlaced, formulas } = record
  return <QuickbetFormulaComponent betPlaced={betPlaced} formulas={formulas} />
})

export const QuickbetFormulaComponent: React.FC<Props> = ({ betPlaced, formulas }) => {
  if (betPlaced || !formulas.length) {
    return null
  }

  const combinations = formulas.map(form => ({
    name: formulaNames[form.formula as keyof typeof formulaNames] || `Pick ${form.formula}`,
    formula: form.formula,
    numberOfCombinations: form.numberOfCombinations,
    isSelected: form.isSelected,
  }))

  const onClickFormula = (formula: number, wasSelected: boolean) =>
    ToggleFormula({ formula, wasSelected })

  return (
    <FormulaStyled>
      <Accordion title='Formula' theme={null}>
        <ul>
          {combinations.map(({ formula, isSelected, name, numberOfCombinations }) => (
            <li key={formula}>
              <button
                type='button'
                data-quickbet-formula-selected={isSelected}
                onClick={() => onClickFormula(formula, isSelected)}
              >
                <span>{name}</span>
                <span>x{numberOfCombinations}</span>
              </button>
            </li>
          ))}
        </ul>
      </Accordion>
    </FormulaStyled>
  )
}

// =============
// Local Helpers
// =============

const formulaNames = {
  1: 'Singles',
  2: 'Doubles',
  3: 'Trebles',
}

// ======
// Styles
// ======

const FormulaStyled = styled.div({
  background: hexColors.whiteSmoke,

  ul: {
    borderColor: hexColors.veryLightGrey,
    borderStyle: 'solid',
    borderWidth: '1px 0 0 1px',
    display: 'flex',
    listStyle: 'none',
    margin: 0,
    padding: 0,

    li: {
      flex: '1 1 0',
      textAlign: 'center',
      borderColor: hexColors.veryLightGrey,
      borderStyle: 'solid',
      borderWidth: '0 1px 1px 0',
      flexBasis: '100%',

      button: {
        border: 'none',
        fontSize: '1.2rem',
        fontFamily: 'inherit',
        height: '5rem',
        lineHeight: 1.5,
        position: 'relative',
        width: '100%',
        textAlign: 'center',
        padding: 0,
        margin: 0,
        background: hexColors.white,
        color: hexColors.black,

        '&[data-quickbet-formula-selected="true"]': {
          background: hexColors.studio,
          color: hexColors.white,
        },

        '> span': { display: 'block' },
      },
    },
  },
})
